/**
 * Utilities for multiple propagation constraints shared between the MST type
 * and the classes that mimic those in the MPC Inspector.
 */
import { TYPE_INFO } from './mpc_config';

// A path element is anything carrying an `id` — at runtime these are MST
// Dependency/Inheritance references, but the shared helpers and their tests
// only ever read `.id`, so a structural shape keeps both callers happy.
interface PathElement {
  id: string;
}

/**
 * Calculate a fingerprint that can be used to check if a sequence of elements
 * (i.e. a path) is equal to another.
 */
export function fingerprint(elements: readonly PathElement[]): string {
  return elements.map((e) => e.id).join('+');
}

/**
 * Check if a path is part of another path.
 *
 * @param elements the elements of the path
 * @param otherElements the elements of the path that we check against
 *
 * @return true if elements are contained in otherElements, false otherwise
 */
export function pathIsPartOf(
  elements: readonly PathElement[] | null | undefined,
  otherElements: readonly PathElement[] | null | undefined
): boolean {
  if (
    otherElements == null ||
    otherElements.length === 0 ||
    elements == null ||
    elements.length === 0
  ) {
    return false;
  }
  const ourFingerprint = fingerprint(elements);
  const theirFingerprint = fingerprint(otherElements);

  return theirFingerprint.includes(ourFingerprint);
}

/**
 * Check if a path starts with another path
 *
 * @param elements the elements of the path
 * @param otherElements the elements of the path that we check against
 *
 * @return true if elements starts with otherElements, false otherwise
 */
export function pathStartsWith(
  elements: readonly PathElement[] | null | undefined,
  otherElements: readonly PathElement[] | null | undefined
): boolean {
  if (
    otherElements == null ||
    otherElements.length === 0 ||
    elements == null ||
    elements.length === 0
  ) {
    return false;
  }
  const ourFingerprint = fingerprint(elements);
  const theirFingerprint = fingerprint(otherElements);

  return ourFingerprint.startsWith(theirFingerprint);
}

/**
 * Check if a path ends with another path
 *
 * @param elements the elements of the path
 * @param otherElements the elements of the path that we check against
 *
 * @return true if elements ends with otherElements, false otherwise
 */
export function pathEndsWith(
  elements: readonly PathElement[] | null | undefined,
  otherElements: readonly PathElement[] | null | undefined
): boolean {
  if (
    otherElements == null ||
    otherElements.length === 0 ||
    elements == null ||
    elements.length === 0
  ) {
    return false;
  }
  const ourFingerprint = fingerprint(elements);
  const theirFingerprint = fingerprint(otherElements);

  return ourFingerprint.endsWith(theirFingerprint);
}

/**
 * Equivalent to pathEndsWith (otherElements, elements)
 */
export function pathIsTailOf(
  elements: readonly PathElement[] | null | undefined,
  otherElements: readonly PathElement[] | null | undefined
): boolean {
  return pathEndsWith(otherElements, elements);
}

/**
 * Equivalent to pathStartsWith (otherElements, elements)
 */
export function pathIsHeadOf(
  elements: readonly PathElement[] | null | undefined,
  otherElements: readonly PathElement[] | null | undefined
): boolean {
  return pathStartsWith(otherElements, elements);
}

/**
 * Check whether two paths have overlapping tails
 *
 * @param elements the elements of the path
 * @param otherElements the elements of the path that we check against
 *
 * @return the common tail of the two paths
 */
export function overlappingTail(
  elements: readonly PathElement[],
  otherElements: readonly PathElement[]
): PathElement[] {
  const commonTail: PathElement[] = [];
  const l = Math.min(elements.length, otherElements.length);
  const reversedElements = elements.slice().reverse();
  const reversedOtherElements = otherElements.slice().reverse();
  for (let i = 0; i < l; ++i) {
    if (reversedElements[i].id === reversedOtherElements[i].id) {
      commonTail.push(reversedElements[i]);
    } else {
      break;
    }
  }
  return commonTail.reverse();
}

/**
 * Check whether a constraint disables a path
 *
 * @param constraint the constraint
 * @param elements the elements in the path to check
 *
 * @return true if the constraint disables the path, false otherwise
 */
export function constraintDisablesPath(
  // Structural shape: this helper is shared between the MST MultiplePropagationConstraint
  // and the plain classes that mimic it in the MPC Inspector, so it only relies on the
  // `type` discriminator and a `secondaryPath` that can test path membership.
  constraint: { type: string; secondaryPath: { isPartOf(elements: readonly PathElement[]): boolean } },
  elements: readonly PathElement[]
): boolean {
  if (constraint.type === TYPE_INFO.EQUAL_TO.key) {
    // disable all paths that include our secondary path
    return constraint.secondaryPath.isPartOf(elements);
  } else {
    return false;
  }
}
